/**
 * Patient Context Bar API
 *
 * RTK Query endpoints for fetching patient context data
 */

import { baseApi } from '@store/baseApi';

import type { PatientModuleDataResponse, PatientPreRecordingInfoResponse } from '../types';

const PATIENT_CONTEXT_TAG_TYPES = [
  'PatientContext',
  'PatientModuleData',
  'PatientPreRecordingInfo',
] as const;

/**
 * Patient Context Bar API
 * Extends baseApi with patient context endpoints
 */
export const patientContextBarApi = baseApi
  .enhanceEndpoints({ addTagTypes: PATIENT_CONTEXT_TAG_TYPES })
  .injectEndpoints({
    endpoints: builder => ({
      /**
       * Get patient module data (medications, diagnoses, allergies)
       */
      getPatientModuleData: builder.query<PatientModuleDataResponse, { patientId: string }>({
        query: ({ patientId }) => ({
          url: `/patient/getPatientData?patient_id=${patientId}`,
          method: 'GET',
        }),
        providesTags: (result, error, { patientId }) => [
          { type: 'PatientModuleData', id: patientId },
          { type: 'PatientContext', id: patientId },
        ],
      }),

      /**
       * Get patient pre-recording information (last encounter, visit summary)
       */
      getPatientPreRecordingInfo: builder.query<
        PatientPreRecordingInfoResponse,
        { patientId: string }
      >({
        query: ({ patientId }) => ({
          url: `/patient/getPreRecordingInformation?patient_id=${patientId}`,
          method: 'GET',
        }),
        providesTags: (result, error, { patientId }) => [
          { type: 'PatientPreRecordingInfo', id: patientId },
          { type: 'PatientContext', id: patientId },
        ],
      }),

      /**
       * Update patient module data (generic endpoint for medications, diagnoses, etc.)
       */
      updatePatientModule: builder.mutation<
        void,
        {
          patientId: string;
          patientModuleId: string;
          dataKey: string;
          data: any[];
        }
      >({
        query: ({ patientId, patientModuleId, dataKey, data }) => ({
          url: '/patient/updatePatientModule',
          method: 'POST',
          body: {
            patient_id: patientId,
            patient_module_id: patientModuleId,
            data: { [dataKey]: data },
          },
        }),
        invalidatesTags: (result, error, { patientId }) => [
          { type: 'PatientModuleData', id: patientId },
          { type: 'PatientContext', id: patientId },
        ],
      }),
    }),
    overrideExisting: false,
  });

// Export user-friendly hook names
export const {
  useGetPatientModuleDataQuery,
  useLazyGetPatientModuleDataQuery,
  useGetPatientPreRecordingInfoQuery,
  useLazyGetPatientPreRecordingInfoQuery,
  useUpdatePatientModuleMutation,
} = patientContextBarApi;
